import { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { isSingleEventActivity, type ActivitySessionDetail } from 'shared';
import { useUpdateSession } from '@/hooks/useActivities';
import { lastDay } from '@/lib/activityTime';
import { ApiValidationError } from '@/lib/api';
import {
  MAX_FIELD,
  MAX_HEADCOUNT,
  SERVER_FIELD,
  buildSessionUpdate,
  toForm,
  type DetailsForm,
  type FormErrors,
} from '@/lib/sessionDetails';
import { FormField } from './ActivityUi';
import { useStaffOptions } from './useStaffOptions';

interface SessionDetailsFormProps {
  session: ActivitySessionDetail;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

/**
 * When the session happens, who runs it, and the visiting group. Visitors
 * have no accounts, so they are counted here rather than named. An open day
 * is edited here too: this is where its date is, and it may last several days.
 */
export default function SessionDetailsForm({ session, onSuccess, onError }: SessionDetailsFormProps) {
  const update = useUpdateSession();
  const staff = useStaffOptions();

  // Read once: the session is refetched every few seconds for the roster, and
  // that must not overwrite what is being typed.
  const [form, setForm] = useState<DetailsForm>(() => toForm(session));
  const [errors, setErrors] = useState<FormErrors>({});
  const openDay = isSingleEventActivity(session.activityType);

  const set = <K extends keyof DetailsForm>(key: K, value: DetailsForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const leaderMissing = !staff.options.some((s) => s.id === form.leaderUserId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { errors: clientErrors, input } = buildSessionUpdate(form, openDay);
    if (input === null) {
      setErrors(clientErrors);
      return;
    }
    try {
      await update.mutateAsync({ id: session.id, data: input });
      onSuccess(openDay ? 'Dia Aberto actualizado com sucesso.' : 'Sessão actualizada com sucesso.');
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setErrors(
          Object.entries(err.errors).reduce<FormErrors>((acc, [field, message]) => {
            const key = SERVER_FIELD[field];
            return key ? { ...acc, [key]: message } : acc;
          }, {}),
        );
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  const invalid = (key: keyof DetailsForm) =>
    errors[key] ? 'border-destructive focus:ring-destructive/30' : '';

  return (
    <section className="card p-6" aria-labelledby="session-details-heading">
      <h2 id="session-details-heading" className="text-lg font-semibold text-foreground">
        {openDay ? 'Detalhes do Dia Aberto' : 'Detalhes da sessão'}
      </h2>

      <form onSubmit={handleSubmit} noValidate className="mt-4 space-y-4">
        <div className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${openDay ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          <FormField
            label={openDay ? 'Primeiro dia' : 'Data'}
            htmlFor="details-date"
            required
            error={errors.date}
          >
            <input
              id="details-date"
              type="date"
              className={`input ${invalid('date')}`}
              value={form.date}
              onChange={(e) => {
                // The last day never falls before the first.
                const date = e.target.value;
                set('date', date);
                if (form.endDate && form.endDate <= date) set('endDate', '');
              }}
              aria-invalid={!!errors.date}
            />
          </FormField>
          {openDay && (
            <FormField label="Último dia" htmlFor="details-end-date">
              <input
                id="details-end-date"
                type="date"
                className="input"
                value={lastDay(form)}
                min={form.date}
                onChange={(e) => {
                  set('endDate', e.target.value === form.date ? '' : e.target.value);
                  // How long it lasts is reported against its end time.
                  if (errors.end) setErrors((prev) => ({ ...prev, end: undefined }));
                }}
              />
            </FormField>
          )}
          <FormField label="Início" htmlFor="details-start" required error={errors.start}>
            <input
              id="details-start"
              type="time"
              className={`input ${invalid('start')}`}
              value={form.start}
              onChange={(e) => set('start', e.target.value)}
              aria-invalid={!!errors.start}
            />
          </FormField>
          <FormField label="Fim" htmlFor="details-end" required error={errors.end}>
            <input
              id="details-end"
              type="time"
              className={`input ${invalid('end')}`}
              value={form.end}
              onChange={(e) => set('end', e.target.value)}
              aria-invalid={!!errors.end}
            />
          </FormField>
        </div>

        <FormField
          label="Dinamizador"
          htmlFor="details-leader"
          error={errors.leaderUserId}
          hint={
            openDay
              ? 'Qualquer pessoa da equipa pode dinamizar o Dia Aberto.'
              : 'Qualquer pessoa da equipa pode dar esta sessão.'
          }
        >
          <select
            id="details-leader"
            className="input"
            value={form.leaderUserId}
            onChange={(e) => set('leaderUserId', Number(e.target.value))}
          >
            {leaderMissing && <option value={form.leaderUserId}>{session.leaderName}</option>}
            {staff.options.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormField>

        <fieldset className="space-y-4 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-medium text-foreground">
            {openDay ? 'Visitantes' : 'Grupo visitante'}
          </legend>
          <p className="text-xs text-muted-foreground">
            {openDay
              ? 'O público de um Dia Aberto não tem conta na app. Conta-se quantos vieram, ao todo; não se registam nomes aqui.'
              : 'Para grupos sem conta na app, como uma turma num Circuito. Conta-se quantos são; não se registam nomes aqui.'}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Escola ou entidade"
              htmlFor="details-organization"
              error={errors.visitorOrganization}
            >
              <input
                id="details-organization"
                className={`input ${invalid('visitorOrganization')}`}
                value={form.visitorOrganization}
                onChange={(e) => set('visitorOrganization', e.target.value)}
                maxLength={MAX_FIELD}
                placeholder="ex. EB 2,3 Martim de Freitas"
              />
            </FormField>
            <FormField
              label="Responsável pelo grupo"
              htmlFor="details-contact"
              error={errors.visitorContact}
            >
              <input
                id="details-contact"
                className={`input ${invalid('visitorContact')}`}
                value={form.visitorContact}
                onChange={(e) => set('visitorContact', e.target.value)}
                maxLength={MAX_FIELD}
                placeholder="Nome e contacto"
              />
            </FormField>
          </div>

          <FormField
            label="Número de visitantes"
            htmlFor="details-headcount"
            error={errors.visitorHeadcount}
          >
            <input
              id="details-headcount"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_HEADCOUNT}
              step={1}
              className={`input w-40 ${invalid('visitorHeadcount')}`}
              value={form.visitorHeadcount}
              onChange={(e) => set('visitorHeadcount', e.target.value)}
              aria-invalid={!!errors.visitorHeadcount}
            />
          </FormField>
        </fieldset>

        <FormField label="Notas" htmlFor="details-notes" error={errors.notes}>
          <textarea
            id="details-notes"
            className="input resize-none h-24"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder={
              openDay
                ? 'O que correu bem, o que mudar no próximo…'
                : 'O que correu bem, o que falta para a próxima sessão…'
            }
          />
        </FormField>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={update.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {update.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar
          </button>
        </div>
      </form>
    </section>
  );
}
